import React, { useEffect, useCallback, useState, useRef, useMemo } from 'react'
import { Link } from 'react-router-dom'
import Datepicker from '../../../components/common/DatePicker/Datepicker'
import Table from '../../../components/common/Table/Table'
import StatusIconWithTooltip from '../../../components/common/StatusIconWithTooltip'
import { usePrint } from '../../../hooks/usePrint'
import { useProfileDisplay } from '../../../hooks/useProfileDisplay'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  getApproverHistory,
  getApproverHistoryForPrint,
  setApproverHistorySort,
  clearApproverHistory,
  type ApproverHistoryItem,
  type ApproverHistorySortBy,
  type ApproverHistorySortOrder,
} from '../../../store/slices/Approver/approverHistorySlice'
import { exportApproverRequests } from '../../../store/slices/Approver/approverExportSlice'
import { convertMMDDYYYYToYYYYMMDD } from '../../../utils/date'
import Loader from '../../../components/common/Loader'

// Asset paths (Vite public assets)
const Print = '/assets/images/printer-outline.svg'
const downIcon = '/assets/images/chevrondown.svg'
const exportIcon = '/assets/images/download-outline.svg'

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

/** Format a Date object as YYYY-MM-DD for API query parameters. */
const formatDateISO = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

/** Format a Date object as MM/DD/YYYY for datepicker controlled value. */
const formatDateMMDDYYYY = (date: Date): string =>
  `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`

// ---------------------------------------------------------------------------
// Row transformation
// ---------------------------------------------------------------------------

/**
 * Transform raw API history items into table rows.
 * The `dateRequested` column holds a clickable link to the request detail
 * page; the status icon is rendered beside the employee NUID instead.
 *
 * @param items - History items returned by the API.
 * @returns Array of rows shaped for the Table component.
 */
const buildHistoryTableRows = (items: ApproverHistoryItem[]) =>
  items.map((item) => ({
    id: item.requestId,
    employeeNuid: (
      <div className="flex items-center justify-between gap-2">
        <span>{item.employeeName}</span>
        <StatusIconWithTooltip statusCode={item.statusCode} alt={item.status} />
      </div>
    ),
    dateRequested: (
      <Link
        to={`/approver/process/${item.requestId}`}
        className="break-normal"
        style={{ color: '#0074AD' }}
      >
        {item.toDate}
      </Link>
    ),
    hoursRequested: item.hoursRequested,
    requestType: item.timeOffReqstType,
    status: item.status,
    approver: item.approverName,
    shift: item.shift,
    rank: item.rank,
  }))

// ---------------------------------------------------------------------------
// Subcomponent: List View Filters
// ---------------------------------------------------------------------------

interface ListFiltersProps {
  startDate: string
  endDate: string
  onStartDateChange: (date: string) => void
  onEndDateChange: (date: string) => void
  onFilterClick: () => void
  onPrint?: () => void
  onExport?: () => void
  /** When set, disables all End Date calendar days before this date (MM/DD/YYYY). */
  minEndDate?: string
  /** When set, disables all Start Date calendar days after this date (MM/DD/YYYY). */
  maxStartDate?: string
  /** Inline validation error for the Start Date field. */
  startDateError?: string
  /** Inline validation error for the End Date field. */
  endDateError?: string
}

/**
 * Renders the date range filter controls for the history view.
 * Both datepickers allow past dates and disable future dates so the approver
 * can only query historical data. Today is disabled after each calendar popup
 * renders because the shared Datepicker intentionally keeps today enabled.
 */
const ListFilters: React.FC<ListFiltersProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onFilterClick,
  onPrint,
  onExport,
  minEndDate,
  maxStartDate,
  startDateError,
  endDateError,
}) => {
  const dateFiltersRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const dateFilters = dateFiltersRef.current
    if (!dateFilters) return

    const disableTodayButtons = () => {
      const todayMarkers = dateFilters.querySelectorAll<HTMLSpanElement>(
        '.day button > span.absolute.bottom-0'
      )

      todayMarkers.forEach((todayMarker) => {
        const todayButton = todayMarker.parentElement
        if (!(todayButton instanceof HTMLButtonElement)) return

        todayButton.disabled = true
        todayButton.setAttribute('aria-disabled', 'true')
        todayButton.style.color = '#d1d5db'
        todayButton.style.cursor = 'not-allowed'
        todayButton.classList.remove('cursor-pointer', 'font-semibold')
        todayButton.classList.add('cursor-not-allowed')
        todayMarker.hidden = true
      })
    }

    disableTodayButtons()
    const observer = new MutationObserver(disableTodayButtons)
    observer.observe(dateFilters, { childList: true, subtree: true })

    return () => observer.disconnect()
  }, [])

  return (
    <div ref={dateFiltersRef}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4 mb-4">
      <div className="history-print-text justify-start text-black text-sm desktop:text-base leading-6 !pt-4">
        Select a date range to display requests that have the specified Start
        Date and End Date.
      </div>
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-start md:justify-end w-full md:w-auto gap-2">
        {onPrint && (
          <button type='button'
            className="button -secondary flex items-center w-full md:w-auto !bg-[#F2F8FB] hover:!bg-[#003B71] active:!text-[#003B71] focus:!text-[#003B71]"
            onClick={onPrint}
            title="Print"
          >
            <img src={Print} alt="Print Icon" className="inline-block mr-2" />
            <span className="text-sm leading-5">Print</span>
          </button>
        )}

        {onExport && (
          <button type="button"
            className="button -secondary flex items-center w-full md:w-auto !bg-[#F2F8FB] hover:!bg-[#003B71] active:!text-[#003B71] focus:!text-[#003B71]"
            onClick={onExport}
            title="Export File"
          >
            <img
              src={exportIcon}
              alt="Export Icon"
              className="inline-block mr-2"
            />
            <span className="text-sm leading-5">Export File</span>
          </button>
        )}
      </div>
    </div>
    <div className="flex flex-col md:flex-row md:flex-wrap xl:flex-nowrap gap-4 md:gap-8 !pt-0 desktop:!pt-[20px] !pb-[20px] history-date-filter">
      <div className="w-full md:w-72">
        <Datepicker
          name="historyStartDate"
          label="Start Date"
          openBehaviour="iconView"
          helperText="MM/DD/YYYY"
          value={startDate}
          disableFutureDates={true}
          disableAfterDate={maxStartDate}
          error={startDateError}
          onChange={(_name, value) =>
            onStartDateChange(Array.isArray(value) ? value[0] : value || '')
          }
        />
      </div>
      <div className="w-full md:w-72">
        <Datepicker
          name="historyEndDate"
          label="End Date"
          openBehaviour="iconView"
          helperText="MM/DD/YYYY"
          value={endDate}
          disableFutureDates={true}
          disableBeforeDate={minEndDate}
          error={endDateError}
          onChange={(_name, value) =>
            onEndDateChange(Array.isArray(value) ? value[0] : value || '')
          }
        />
      </div>
      <div className="!pt-[20px] desktop:!pt-[25px]">
        <button type="button"
          className="button -secondary whitespace-nowrap pb-1"
          onClick={onFilterClick}
        >
          Filter Dates
        </button>
      </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Subcomponent: Results Table
// ---------------------------------------------------------------------------

interface ResultsTableProps {
  columns: {
    key: string
    label: string
    className?: string
    icon?: string
    isSortable?: boolean
    sortKey?: string
  }[]
  rows: ReturnType<typeof buildHistoryTableRows>
  totalItems: number
  itemsPerPage: number
  currentPage: number
  onPageChange: (page: number) => void
  onSort: (columnKey: string, sortDirection: 'ASC' | 'DESC') => void
  sortColumn?: string
  sortDirection?: 'ASC' | 'DESC'
}

/**
 * Renders the paginated, sortable results table for the history view.
 * Pagination and sorting are server-driven — callbacks are required.
 */
const ResultsTable: React.FC<ResultsTableProps> = ({
  columns,
  rows,
  totalItems,
  itemsPerPage,
  currentPage,
  onPageChange,
  onSort,
  sortColumn,
  sortDirection,
}) => (
  <div className="mt-6">
    <Table
      columns={columns}
      rows={rows}
      isSorting={true}
      showPagination={true}
      paginationProps={{ totalItems, itemsPerPage }}
      currentPage={currentPage}
      onPageChange={onPageChange}
      onSort={onSort}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
    />
  </div>
)

// ---------------------------------------------------------------------------
// Main component: ApproverHistory
// ---------------------------------------------------------------------------

/**
 * Session-storage keys for persisting the last successful filter dates.
 * On refresh the History tab re-uses the dates instead of resetting to yesterday.
 */
const STORAGE_KEY_START = 'approverHistoryStartDate'
const STORAGE_KEY_END = 'approverHistoryEndDate'

/** Validation message shown on the Start Date field when it is empty. */
const START_DATE_ERROR = 'Please select Current or Past Date.'
/** Validation message shown on the End Date field when it is empty or before Start Date. */

/**
 * Approver History page.
 *
 * On mount, fetches one page of history using today as the reference date and
 * the last-saved (or yesterday as fallback) start/end dates.  The Filter Dates
 * button re-fetches with the dates selected in the datepickers.  The three
 * columns empNUID, shift, and rank support server-side sorting; pagination is
 * also server-side.
 *
 * @param updateDataCount - Optional callback supplied by the parent Tabs shell
 *   to update the History tab badge with the live total record count.
 */
interface ApproverHistoryProps {
  updateDataCount?: (tabId: number, count: number) => void
}

const ApproverHistory: React.FC<ApproverHistoryProps> = ({
  updateDataCount,
}) => {
  const dispatch = useAppDispatch()
  const {
    items,
    loading,
    error,
    currentPage,
    pageSize,
    totalRecords,
    sortBy,
    sortOrder,
  } = useAppSelector((state) => state.approverHistory)

  const exportLoading = useAppSelector((state) => state.approverExport.loading)
  const today = useMemo(() => new Date(), [])
  const yesterday = useMemo(() => {
    const d = new Date(today)
    d.setDate(d.getDate() - 1)
    return d
  }, [today])
  const todayFormatted = useMemo(() => formatDateMMDDYYYY(today), [today])

  // Datepicker controlled values (MM/DD/YYYY).
  // Prefer the last saved dates from sessionStorage so a page refresh preserves
  // the user's previously selected filter range; fall back to yesterday.
  const [startDate, setStartDate] = useState(
    () =>
      sessionStorage.getItem(STORAGE_KEY_START) || formatDateMMDDYYYY(yesterday)
  )
  const [endDate, setEndDate] = useState(
    () =>
      sessionStorage.getItem(STORAGE_KEY_END) || formatDateMMDDYYYY(yesterday)
  )

  const [showRank, setShowRank] = useState(false)

  /** Inline validation error for the Start Date field. */
  const [startDateError, setStartDateError] = useState<string | undefined>(
    undefined
  )
  /** Inline validation error for the End Date field. */
  const [endDateError, setEndDateError] = useState<string | undefined>(
    undefined
  )
  const [printRows, setPrintRows] = useState<any[]>([])
  const [isPrinting, setIsPrinting] = useState(false)
  /**
   * Retains the ISO start/end dates used by the most recent fetch so that
   * pagination and sort always re-use the correct range without re-deriving
   * them from the datepicker UI state.  Seeded from sessionStorage so it
   * matches the restored picker values after a page refresh.
   */
  const activeDatesRef = useRef({
    startISO: convertMMDDYYYYToYYYYMMDD(startDate),
    endISO: convertMMDDYYYYToYYYYMMDD(endDate),
  })

  /** Dispatches a history fetch using ISO date strings. */
  const fetchHistory = useCallback(
    (
      startISO: string,
      endISO: string,
      page: number,
      sortByParam?: ApproverHistorySortBy,
      sortOrderParam?: ApproverHistorySortOrder,
      perPageParam?: number
    ) => {
      dispatch(
        getApproverHistory({
          date: formatDateISO(today),
          startDate: startISO,
          endDate: endISO,
          page,
          perPage: perPageParam ?? pageSize,
          sortBy: sortByParam,
          sortOrder: sortOrderParam,
        })
      )
    },
    [dispatch, pageSize, today]
  )
  const isValidDateFormat = (value: string) => {
    // MM/DD/YYYY
    const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/
    return regex.test(value)
  }

  // On mount: load first page using the saved (or default) date range
  useEffect(() => {
    const { startISO, endISO } = activeDatesRef.current
    fetchHistory(startISO, endISO, 1)
    return () => {
      dispatch(clearApproverHistory())
    }
    // Run only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Push the live total record count to the parent tab badge whenever it changes
  useEffect(() => {
    if (typeof updateDataCount === 'function') {
      updateDataCount(2, totalRecords)
    }
  }, [totalRecords, updateDataCount])

  /** Called when the user clicks "Filter Dates" — validates then sends the picker values to the API. */

  const DATE_RANGE_ERROR =
  "Please select valid Start and End Dates. End date must be '>=' Start Date"

  /**
   * Validates a single date field (NaN, format, future).
   * @returns Error message if invalid, undefined if valid.
   */
  const validateSingleDate = (
    dateStr: string,
    parsedDate: Date,
    todayDate: Date,
    fieldName: 'Start' | 'End'
  ): string | undefined => {
    if (Number.isNaN(parsedDate.getTime())) {
      return `Please enter a valid ${fieldName} Date.`
    }
    if (!isValidDateFormat(dateStr)) {
      return `Please enter a valid ${fieldName} Date.`
    }
    if (parsedDate >= todayDate) {
      return `${fieldName} Date cannot be in the future.`
    }
    return undefined
  }

const handleFilterClick = useCallback(() => {
  // Check required fields first
  if (!startDate || !endDate) {
    setStartDateError(startDate ? undefined : START_DATE_ERROR)
    setEndDateError(endDate ? undefined : DATE_RANGE_ERROR)
    return
  }

  const start = new Date(startDate)
  const end = new Date(endDate)
  const todayDate = new Date()

  start.setHours(0, 0, 0, 0)
  end.setHours(0, 0, 0, 0)
  todayDate.setHours(0, 0, 0, 0)

  // Validate each date field
  const startError = validateSingleDate(startDate, start, todayDate, 'Start')
  const endError = validateSingleDate(endDate, end, todayDate, 'End')

  // Date range validation (only if both dates are individually valid)
  const rangeError = !startError && !endError && end < start ? DATE_RANGE_ERROR : endError

  setStartDateError(startError)
  setEndDateError(rangeError)

  if (startError || rangeError) {
    return
  }

  const startISO = convertMMDDYYYYToYYYYMMDD(startDate)
  const endISO = convertMMDDYYYYToYYYYMMDD(endDate)

  sessionStorage.setItem(STORAGE_KEY_START, startDate)
  sessionStorage.setItem(STORAGE_KEY_END, endDate)

  activeDatesRef.current = { startISO, endISO }

  fetchHistory(
    startISO,
    endISO,
    1,
    sortBy,
    sortOrder
  )
}, [startDate, endDate, fetchHistory, sortBy, sortOrder])

  /** Called when the user navigates to a different page in the table. */
  const handlePageChange = useCallback(
    (page: number) => {
      const { startISO, endISO } = activeDatesRef.current
      fetchHistory(
  startISO,
  endISO,
  page,
  sortBy,
  sortOrder
)
    },
    [fetchHistory, sortBy, sortOrder]
  )

  /** Called when the user clicks a sortable column header — stays on the current page. */
  const handleSort = useCallback(
    (columnKey: string, sortDirection: 'ASC' | 'DESC') => {
      const newSortBy = columnKey as ApproverHistorySortBy
      dispatch(
        setApproverHistorySort({ sortBy: newSortBy, sortOrder: sortDirection })
      )
      const { startISO, endISO } = activeDatesRef.current
      fetchHistory(startISO, endISO, currentPage, newSortBy, sortDirection)
    },
    [dispatch, fetchHistory, currentPage]
  )

  /** Clears start-date error and updates the controlled value — extracted to avoid multi-statement inline lambdas in JSX. */
  const handleStartDateChange = useCallback((v: string) => {
    if (v === todayFormatted) return
    setStartDate(v)
    setStartDateError(undefined)
  }, [todayFormatted])

  /** Clears end-date error and updates the controlled value — extracted to avoid multi-statement inline lambdas in JSX. */
  const handleEndDateChange = useCallback((v: string) => {
    if (v === todayFormatted) return
    setEndDate(v)
    setEndDateError(undefined)
  }, [todayFormatted])

  /** Memoised column definitions — only recomputed when the rank toggle changes. */
  const columns = useMemo(() => {
    const baseColumns = [
      {
        key: 'employeeNuid',
        label: 'Employee (NUID)',
        className: 'md:!min-w-[130px]',
        icon: downIcon,
        isSortable: true,
        sortKey: 'empNUID',
      },
      {
        key: 'dateRequested',
        label: 'Dates\nRequested',
        className: '!whitespace-pre-wrap md:!min-w-[110px] md:!max-w-[130px]',
        icon: downIcon,
        isSortable: true,
        sortKey: 'dateRequested',
      },
      {
        key: 'hoursRequested',
        label: 'Hours\nRequested',
        className: '!whitespace-pre-wrap md:!min-w-[100px]',
        icon: downIcon,
        isSortable: true,
        sortKey: 'hoursRequested',
      },
      {
        key: 'requestType',
        label: 'Request Type',
        className: 'md:!min-w-[140px]',
        icon: downIcon,
        isSortable: true,
        sortKey: 'requestType',
      },
      { key: 'status', label: 'Request Status', className: 'md:!min-w-[140px]', icon: downIcon,
        isSortable: true,
        sortKey: 'status',
      },
      {
        key: 'approver',
        label: 'Approver',
        className: '!whitespace-pre-wrap md:!min-w-[120px]',
        icon: downIcon,
        isSortable: true,
        sortKey: 'approver',
      },
      {
        key: 'shift',
        label: 'Shift',
        className: '!whitespace-nowrap md:!min-w-[90px]',
        icon: downIcon,
        isSortable: true,
        sortKey: 'shift',
      },
      {
        key: 'rank',
        label: 'Vac Bid Rank',
        className: '!whitespace-pre-wrap md:!min-w-[100px]',
        icon: downIcon,
        isSortable: true,
        sortKey: 'rank',
      },
    ]
    return showRank
      ? baseColumns
      : baseColumns.filter((col) => col.key !== 'rank')
  }, [showRank])

  const printColumns = useMemo(
  () =>
    columns.map((column) => ({
      ...column,
      icon: undefined,
      isSortable: false,
    })),
  [columns]
)

  const rows = buildHistoryTableRows(items)

  const { printRef, handlePrint } = usePrint({ fontSize: '9px' })
  const { userInfo, userNuid, primaryApproverInfo } = useProfileDisplay()

 const handlePrintWithApi = async () => {
  try {
    setIsPrinting(true)

    const { startISO, endISO } = activeDatesRef.current

   const response = await dispatch(
  getApproverHistoryForPrint({
    date: formatDateISO(today),
    startDate: startISO,
    endDate: endISO,
    page: 1,
    perPage: totalRecords,
    sortBy: sortBy,
    sortOrder: sortOrder,
  })
).unwrap()

    setPrintRows(buildHistoryTableRows(response.items))

    setTimeout(() => {
      handlePrint()
    }, 100)
  } catch (error) {
    console.error(error)
  } finally {
    setIsPrinting(false)
  }
}
  const handleExport = async () => {
    const { startISO, endISO } = activeDatesRef.current

    await dispatch(
      exportApproverRequests({
        type: 'history',
        payload: {
          date: formatDateISO(today),
          startDate: startISO,
          endDate: endISO,
          sortBy: sortBy,
          sortOrder: sortOrder,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      })
    )
  }

  return (
    <div className="space-y-6">
<style>
{`
/* Mobile only: Ensure date pickers match button full width */
@media screen and (max-width: 767px) {
  .history-date-filter > div,
  .history-date-filter .date-field-wrapper,
  .history-date-filter .date-input-field,
  .history-date-filter input {
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
  }
}

#temp-print-container {
  display: none;
}

@media print {
  @page {
    size: landscape;
    margin: 10mm;
  }

  /* Hide normal screen content */
  .approver-history-print,
  .history-filter-section,
  button {
    display: none !important;
  }

  /* Show print table */
  #temp-print-container {
    display: block !important;
    width: 100% !important;
    max-width: 100% !important;
    overflow: visible !important;
  }

  /* Remove scroll/fixed sizing from shared Table */
  #temp-print-container,
  #temp-print-container * {
    overflow: visible !important;
  }

  #temp-print-container table {
    width: 100% !important;
    max-width: 100% !important;
    table-layout: auto !important;
    border-collapse: collapse !important;
  }

  /* Repeat header on every printed page */
  #temp-print-container thead {
    display: table-header-group !important;
  }

  #temp-print-container tbody {
    display: table-row-group !important;
  }

  #temp-print-container tr {
    page-break-inside: avoid !important;
  }

 #temp-print-container th {
  white-space: normal !important;
  word-break: break-word !important;
  padding: 6px !important;
  font-size: 13px !important;   /* Increase header font size */
  font-weight: 700 !important;   /* Make header bold */
}

#temp-print-container td {
  white-space: normal !important;
  word-break: break-word !important;
  padding: 4px !important;
  font-size: 9px !important;    /* Keep body rows smaller */
}

  /* Hide sorting arrows in print */
  #temp-print-container .arrows-table {
    display: none !important;
  }

  /* Remove pagination area from print */
  #temp-print-container .pagination {
    display: none !important;
  }
}
  @media print {
  @page {
    size: landscape;
    margin: 10mm;
  }

  #temp-print-container {
    display: block !important;
    width: 100% !important;
    overflow: visible !important;
  }

  #temp-print-container table {
    width: 100% !important;
    table-layout: fixed !important;
    border-collapse: collapse !important;
  }

  /* Repeat table header on every page */
  #temp-print-container thead {
    display: table-header-group !important;
  }

  #temp-print-container tr {
    page-break-inside: avoid !important;
  }

  /* Important: keep header content visible */
  #temp-print-container th {
    display: table-cell !important;
    visibility: visible !important;
    overflow: visible !important;
    white-space: normal !important;
  }

  #temp-print-container th img {
    display: none !important;
  }

  #temp-print-container th span {
    display: inline !important;
    visibility: visible !important;
  }
}
`}
</style>
    
      {/* Date range filters and print button */}
      <div className="history-filter-section">
        <ListFilters
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
          onFilterClick={handleFilterClick}
          onPrint={handlePrintWithApi}
          onExport={handleExport}
          minEndDate={startDate}
          maxStartDate={endDate}
          startDateError={startDateError}
          endDateError={endDateError}
        />
      </div>
      {/* Full-screen loading overlay — shown during every API fetch */}
      {(loading || exportLoading || isPrinting) && <Loader />}

      {/* API error banner */}
      {error && (
        <div className="text-red-600 text-sm mt-2" role="alert">
          {error}
        </div>
      )}

      {/* Seniority rank toggle */}
      <div className="flex items-center justify-end mb-4">
        <button
          className="hidden md:flex items-center gap-2 px-4 py-1"
          onClick={() => setShowRank(!showRank)}
          title={showRank ? 'Hide Vac Bid Rank' : 'Show Vac Bid Rank'}
        >
          <img
            src={
              showRank
                ? '/assets/images/eye-off-outline.svg'
                : '/assets/images/eye-frame-outline.svg'
            }
            alt={showRank ? 'Hide' : 'Show'}
            className="w-5 h-5"
          />
          <span className="p2 -medium text-[#0074AD]">
            {showRank ? 'Hide' : 'Show'} Vac Bid Rank
          </span>
        </button>
      </div>

      {/* Results table — visible immediately on page load */}
      <div className="approver-history-print">
        <ResultsTable
          columns={columns}
          rows={rows}
          totalItems={totalRecords}
          itemsPerPage={pageSize}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onSort={handleSort}
          sortColumn={sortBy}
          sortDirection={sortOrder}
        />
      </div>
      {printRows.length > 0 && (
        <div ref={printRef} id="temp-print-container">
          <div style={{marginBottom: '16px', paddingBottom: '8px', borderBottom: '1px solid #ccc'}}>
            <p style={{margin: '0 0 4px 0', fontWeight: 'bold', fontSize: '14px'}}>{userInfo}{userNuid}</p>
            <p style={{margin: '0 0 4px 0', fontSize: '12px'}}>Primary Approver: {primaryApproverInfo}</p>
            <p style={{margin: '0 0 4px 0', fontSize: '12px'}}>History</p>
            <p style={{margin: '0', fontSize: '12px'}}>Date Range: {startDate} - {endDate}</p>
          </div>
          <ResultsTable
            columns={printColumns}
            rows={printRows}
            totalItems={printRows.length}
            itemsPerPage={printRows.length}
            currentPage={1}
            onPageChange={() => {}}
            onSort={() => {}}
          />
        </div>
      )}
    </div>
  )
}

export default ApproverHistory
